import { BlockComponent } from '@blocksuite/affine/block-std';
import { computed } from '@preact/signals-core';
import { html, nothing, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import {
  type AgentRunState,
  deserialiseAgentRunState,
  type StageEntry,
} from '../../../../modules/orchestration/state/agent-run-state';
import type {
  StepIcon,
  ToolUseSource,
} from '../../../../modules/orchestration/types';
import type { AgentStagesBlockModel } from './model';
import { agentStagesStyles } from './styles';

export const AGENT_STAGES_CANCEL_EVENT = 'agent-stages-cancel';

export interface AgentStagesCancelDetail {
  planId?: string;
}

// Mode → CSS-variable palette. BlockComponent renders into light DOM,
// so `:host` selectors and `[data-mode]` attribute selectors inside the
// scoped stylesheet never apply. We set the variables imperatively on
// the element style in `renderBlock` so the cascade works.
const MODE_PALETTES = {
  explore: {
    fg: '#1e3a8a',
    bg: 'rgba(30, 58, 138, 0.08)',
    bgStrong: 'rgba(30, 58, 138, 0.16)',
    softBg: 'rgba(30, 58, 138, 0.04)',
    ring: 'rgba(30, 58, 138, 0.4)',
  },
  analyze: {
    fg: '#0d9488',
    bg: 'rgba(13, 148, 136, 0.08)',
    bgStrong: 'rgba(13, 148, 136, 0.16)',
    softBg: 'rgba(13, 148, 136, 0.04)',
    ring: 'rgba(13, 148, 136, 0.4)',
  },
  'deep-research': {
    fg: '#7c3aed',
    bg: 'rgba(124, 58, 237, 0.08)',
    bgStrong: 'rgba(124, 58, 237, 0.16)',
    softBg: 'rgba(124, 58, 237, 0.04)',
    ring: 'rgba(124, 58, 237, 0.4)',
  },
} as const;

type PaletteKey = keyof typeof MODE_PALETTES;
const paletteFor = (mode?: string): (typeof MODE_PALETTES)[PaletteKey] =>
  (MODE_PALETTES as Record<string, (typeof MODE_PALETTES)[PaletteKey]>)[
    mode ?? 'explore'
  ] ?? MODE_PALETTES.explore;

// Monochrome line icons — same family as the rest of the product.
const ICON_SEARCH = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <circle cx="7" cy="7" r="4.5" />
  <line x1="10.5" y1="10.5" x2="14" y2="14" />
</svg>`;
const ICON_FILE = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M3 2.5 H9 L13 6.5 V13.5 H3 Z" />
  <path d="M9 2.5 V6.5 H13" />
</svg>`;
const ICON_EDIT = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M11 3 L13 5 L5.5 12.5 L3 13 L3.5 10.5 Z" />
</svg>`;
const ICON_GLOBE = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  aria-hidden="true"
>
  <circle cx="8" cy="8" r="5.5" />
  <path d="M2.5 8 H13.5" />
  <path d="M8 2.5 C10 4.5 10 11.5 8 13.5 C6 11.5 6 4.5 8 2.5 Z" />
</svg>`;
const ICON_TERMINAL = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M3 4.5 L6 8 L3 11.5" />
  <line x1="7.5" y1="12" x2="13" y2="12" />
</svg>`;
const ICON_CHART = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <line x1="3" y1="13" x2="13" y2="13" />
  <rect x="4" y="8" width="2" height="5" />
  <rect x="7.5" y="5" width="2" height="8" />
  <rect x="11" y="9" width="2" height="4" />
</svg>`;
const ICON_BOOKMARK = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M4 2.5 H12 V13.5 L8 10.5 L4 13.5 Z" />
</svg>`;
const ICON_SPARKLES = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M9 2 L10 5 L13 6 L10 7 L9 10 L8 7 L5 6 L8 5 Z" />
  <path d="M4 10 L4.5 11.5 L6 12 L4.5 12.5 L4 14 L3.5 12.5 L2 12 L3.5 11.5 Z" />
</svg>`;
const ICON_LOADING = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  aria-hidden="true"
>
  <path d="M8 2.5 A5.5 5.5 0 1 1 2.5 8" />
</svg>`;
const ICON_DATABASE = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  aria-hidden="true"
>
  <ellipse cx="8" cy="3.5" rx="5" ry="1.8" />
  <path d="M3 3.5 V12.5 C3 13.5 5.2 14.3 8 14.3 C10.8 14.3 13 13.5 13 12.5 V3.5" />
  <path d="M3 8 C3 9 5.2 9.8 8 9.8 C10.8 9.8 13 9 13 8" />
</svg>`;
const ICON_CODE = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M6 4 L2.5 8 L6 12" />
  <path d="M10 4 L13.5 8 L10 12" />
</svg>`;

const STEP_ICON_SVG: Record<StepIcon, TemplateResult> = {
  search: ICON_SEARCH,
  file: ICON_FILE,
  edit: ICON_EDIT,
  globe: ICON_GLOBE,
  terminal: ICON_TERMINAL,
  chart: ICON_CHART,
  bookmark: ICON_BOOKMARK,
  sparkles: ICON_SPARKLES,
  loading: ICON_LOADING,
  database: ICON_DATABASE,
  code: ICON_CODE,
};

const ICON_CHEVRON = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.8"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M6 4 L10 8 L6 12" />
</svg>`;
const ICON_CHEVRON_UP = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.8"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M4 10 L8 6 L12 10" />
</svg>`;
const ICON_CHECK = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.8"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <circle cx="8" cy="8" r="6.5" />
  <path d="M5 8 L7 10 L11 6" />
</svg>`;
const ICON_ALERT = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M8 2 L14.5 13 H1.5 Z" />
  <line x1="8" y1="6.5" x2="8" y2="9.5" />
  <circle cx="8" cy="11.4" r="0.6" fill="currentColor" />
</svg>`;
const ICON_COMPASS = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <circle cx="8" cy="8" r="5.5" />
  <path d="M10.5 5.5 L8.6 8.6 L5.5 10.5 L7.4 7.4 Z" fill="currentColor" />
</svg>`;

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

function modeLabel(mode?: string): string {
  switch (mode) {
    case 'explore':
      return 'Explore';
    case 'analyze':
      return 'Analyze';
    case 'deep-research':
      return 'Deep Research';
    case 'auto':
      return 'Auto';
    default:
      return 'Research';
  }
}

const stopPropagation = (e: Event): void => {
  e.stopPropagation();
};

function sourceDomain(src: ToolUseSource): string {
  if (src.domain) return src.domain;
  try {
    return new URL(src.url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function sourceChipLabel(src: ToolUseSource): string {
  const title = src.title?.trim();
  if (title && title.length > 0) {
    return title.length > 32 ? title.slice(0, 30).trimEnd() + '…' : title;
  }
  const domain = sourceDomain(src);
  return domain || (src.url.length > 32 ? src.url.slice(0, 30) + '…' : src.url);
}

/**
 * Pull the canonical identifier out of a source for the expanded list:
 * PMID:38… for PubMed, DOI:10.…, arXiv:1234.56789, else a short URL.
 * Keeps the expanded sources scannable as a stack of identifiers
 * rather than repeated `pubmed.ncbi.nlm.nih.gov` rows.
 */
function shortSourceId(src: ToolUseSource): string {
  const url = src.url ?? '';
  const pmid = url.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/i);
  if (pmid?.[1]) return pmid[1];
  const doi = url.match(/doi\.org\/(.+)$/i);
  if (doi?.[1]) return `DOI:${decodeURIComponent(doi[1])}`;
  const arxiv = url.match(/arxiv\.org\/(?:abs|pdf)\/([\w.]+)/i);
  if (arxiv?.[1]) return `arXiv:${arxiv[1]}`;
  const title = src.title?.trim();
  if (title && title.length > 0 && !/^https?:\/\//.test(title)) {
    return title.length > 40 ? title.slice(0, 38).trimEnd() + '…' : title;
  }
  return url.length > 40 ? url.slice(0, 38) + '…' : url;
}

// Dot-ring spinner: 12 small dots arranged in a circle, each pulsing
// with a phase offset. Lit doesn't have nice loops in templates, so we
// pre-compute the dot positions and inject them inline.
const DOT_RING_COUNT = 12;
const DOT_RING_ITEMS: TemplateResult[] = Array.from(
  { length: DOT_RING_COUNT },
  (_, i) => {
    const angle = (i / DOT_RING_COUNT) * 2 * Math.PI;
    const radius = 9; // px (slightly inset from a 22px container)
    const dotSize = 3;
    const x = 11 + radius * Math.cos(angle) - dotSize / 2;
    const y = 11 + radius * Math.sin(angle) - dotSize / 2;
    const delay = ((i / DOT_RING_COUNT) * 1.1).toFixed(2);
    return html`<span
      class="live-edge-dot"
      style="left:${x}px; top:${y}px; animation-delay:${delay}s;"
    ></span>`;
  }
);

export class AgentStagesBlockComponent extends BlockComponent<AgentStagesBlockModel> {
  static override styles = agentStagesStyles;

  @state()
  private accessor elapsedMs = 0;

  @state()
  private accessor expandedStageIds = new Set<string>();

  @state()
  private accessor expandedSourceStageIds = new Set<string>();

  private tickerId: ReturnType<typeof setInterval> | null = null;

  private readonly _parsed = computed<AgentRunState | null>(() => {
    const json = this.model.props.stagesJson$.value;
    return deserialiseAgentRunState(json);
  });

  override connectedCallback(): void {
    super.connectedCallback();
    this.style.display = 'block';
    // Wider top gap so the agent-stages block doesn't crowd the user
     // query quote above it (ChatGPT-style breathing room between the
     // question bubble and the response). Bottom stays tight so the
     // synthesis below hugs the stages block as one visual unit.
    this.style.margin = '32px 0 12px';
    this.tickerId = setInterval(() => {
      const runState = this._parsed.value;
      if (!runState) return;
      if (!runState.streaming) {
        this.elapsedMs =
          runState.endedAt !== undefined
            ? runState.endedAt - runState.startedAt
            : 0;
        return;
      }
      this.elapsedMs = Date.now() - runState.startedAt;
    }, 500);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.tickerId !== null) {
      clearInterval(this.tickerId);
      this.tickerId = null;
    }
  }

  private readonly _toggleCollapse = (e: Event): void => {
    e.stopPropagation();
    const current = !!this.model.props.collapsed$.value;
    this.doc.updateBlock(this.model, { collapsed: !current });
  };

  private readonly _cancel = (e: Event): void => {
    e.stopPropagation();
    const runState = this._parsed.value;
    const detail: AgentStagesCancelDetail = { planId: runState?.planId };
    this.dispatchEvent(
      new CustomEvent<AgentStagesCancelDetail>(AGENT_STAGES_CANCEL_EVENT, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  };

  private readonly _onStageChevronClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget as HTMLElement | null;
    const stageId = target?.dataset.stageId;
    if (!stageId) return;
    const next = new Set(this.expandedStageIds);
    if (next.has(stageId)) {
      next.delete(stageId);
    } else {
      next.add(stageId);
    }
    this.expandedStageIds = next;
  };

  private readonly _onSourcesToggleClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget as HTMLElement | null;
    const stageId = target?.dataset.stageId;
    if (!stageId) return;
    const next = new Set(this.expandedSourceStageIds);
    if (next.has(stageId)) {
      next.delete(stageId);
    } else {
      next.add(stageId);
    }
    this.expandedSourceStageIds = next;
  };

  override renderBlock(): TemplateResult | typeof nothing {
    const runState = this._parsed.value;
    if (!runState) return nothing;
    const collapsed = !!this.model.props.collapsed$.value;
    this.setAttribute('data-mode', runState.mode ?? 'explore');
    this.setAttribute('data-collapsed', collapsed ? 'true' : 'false');
    // Apply the mode palette imperatively so child CSS vars resolve
    // even though BlockComponent renders into light DOM (where :host
    // and host-attribute selectors don't take effect).
    const palette = paletteFor(runState.mode);
    this.style.setProperty('--cur-mode-fg', palette.fg);
    this.style.setProperty('--cur-mode-bg', palette.bg);
    this.style.setProperty('--cur-mode-bg-strong', palette.bgStrong);
    this.style.setProperty('--cur-mode-soft-bg', palette.softBg);
    this.style.setProperty('--cur-mode-ring', palette.ring);
    return collapsed
      ? this._renderCollapsed(runState)
      : this._renderExpanded(runState);
  }

  private _renderCollapsed(runState: AgentRunState): TemplateResult {
    const duration =
      runState.endedAt !== undefined
        ? runState.endedAt - runState.startedAt
        : this.elapsedMs;
    const sources = runState.stages.reduce(
      (sum, s) => sum + s.sources.length,
      0
    );
    const agentCount =
      runState.agentsSeen.length > 0
        ? runState.agentsSeen.length
        : runState.stages.length;
    const bits: string[] = [formatElapsed(duration)];
    if (agentCount > 0)
      bits.push(`${agentCount} step${agentCount === 1 ? '' : 's'}`);
    if (sources > 0) bits.push(`${sources} source${sources === 1 ? '' : 's'}`);
    if (runState.totalCitations > 0)
      bits.push(
        `${runState.totalCitations} citation${runState.totalCitations === 1 ? '' : 's'}`
      );

    return html`
      <button
        class="collapsed-row"
        @click=${this._toggleCollapse}
        aria-expanded="false"
        aria-label="Expand research log"
      >
        <span class="chevron" aria-hidden="true">${ICON_CHEVRON}</span>
        <span class="label">Research log</span>
        <span class="meta">· ${bits.join(' · ')}</span>
      </button>
    `;
  }

  private _renderExpanded(runState: AgentRunState): TemplateResult {
    const elapsed =
      runState.endedAt !== undefined
        ? runState.endedAt - runState.startedAt
        : this.elapsedMs;
    // The mode pill is the toggle: clickable once the run finishes so
    // the user can collapse the transcript back into the one-line log.
    // While streaming it stays static (don't let the user nuke the
    // live edge mid-run).
    const modeTag = runState.streaming
      ? html`<span class="mode-tag">
          ${modeLabel(runState.mode)}
          <span class="elapsed">· ${formatElapsed(elapsed)}</span>
        </span>`
      : html`<button
          class="mode-tag mode-tag--toggle"
          @click=${this._toggleCollapse}
          aria-label="Collapse research log"
          title="Collapse research log"
        >
          ${modeLabel(runState.mode)}
          <span class="elapsed">· ${formatElapsed(elapsed)}</span>
          <span class="mode-tag-chevron" aria-hidden="true"
            >${ICON_CHEVRON_UP}</span
          >
        </button>`;
    return html`
      <div class="transcript" role="region" aria-label="Research workflow">
        ${modeTag}
        ${runState.subQuestions.length > 0
          ? this._renderPlan(runState)
          : nothing}
        ${runState.stages.map((stage, i) => this._renderStep(stage, i))}
        ${runState.streaming ? this._renderLiveEdge() : nothing}
        ${runState.errorMessage
          ? html`<div class="error-banner">
              ${ICON_ALERT}<span>${runState.errorMessage}</span>
            </div>`
          : nothing}
        ${!runState.streaming
          ? html`<button
              class="transcript-collapse"
              @click=${this._toggleCollapse}
              aria-label="Collapse research log"
            >
              Collapse research log
            </button>`
          : nothing}
      </div>
    `;
  }

  private _renderPlan(runState: AgentRunState): TemplateResult {
    return html`
      <div class="plan">
        <div class="plan-title">
          ${ICON_COMPASS}
          <span
            >Plan · ${runState.subQuestions.length}
            sub-question${runState.subQuestions.length === 1 ? '' : 's'}</span
          >
        </div>
        <ul class="plan-list">
          ${runState.subQuestions.map(q => html`<li>${q}</li>`)}
        </ul>
      </div>
    `;
  }

  private _renderStep(stage: StageEntry, index: number): TemplateResult {
    const icon = STEP_ICON_SVG[stage.icon] ?? ICON_SPARKLES;
    const durationMs =
      stage.endedAt !== undefined ? stage.endedAt - stage.startedAt : null;
    const hasSources = stage.sources.length > 0;
    const isExpanded = this.expandedStageIds.has(stage.id);

    // Cascade the step entry slightly so re-expanding the transcript
    // unrolls instead of dumping all rows in the same frame.
    const stagger = Math.min(index, 6) * 28;
    const style = `animation-delay: ${stagger}ms;`;

    return html`
      <div
        class="step step--${stage.status}"
        role="listitem"
        style=${style}
      >
        <div class="step-title-row">
          <span class="step-icon" aria-hidden="true">${icon}</span>
          <span class="step-title">${stage.title}</span>
          ${stage.sources.length > 0
            ? html`<span class="step-badge"
                >${stage.sources.length}
                source${stage.sources.length === 1 ? '' : 's'}</span
              >`
            : nothing}
          ${stage.citationsFound && stage.citationsFound > 0
            ? html`<span class="step-badge"
                >${stage.citationsFound} cited</span
              >`
            : nothing}
          ${hasSources || (stage.status === 'done' && stage.lastSubStep)
            ? html`<button
                class="step-chevron"
                data-stage-id=${stage.id}
                data-open=${isExpanded ? 'true' : 'false'}
                @click=${this._onStageChevronClick}
                aria-expanded=${isExpanded ? 'true' : 'false'}
                aria-label=${isExpanded
                  ? 'Collapse step details'
                  : 'Expand step details'}
              >
                ${ICON_CHEVRON}
              </button>`
            : nothing}
        </div>

        <div class="step-body">
          ${stage.status === 'active' && stage.lastSubStep
            ? html`<div class="step-substep">
                <span class="step-substep-text">${stage.lastSubStep}</span>
                ${stage.subStepDetail
                  ? html`<span class="step-substep-detail"
                      >${stage.subStepDetail}</span
                    >`
                  : nothing}
              </div>`
            : nothing}
          ${stage.status === 'done' && isExpanded && hasSources
            ? this._renderSources(stage.id, stage.sources)
            : nothing}
          ${stage.status === 'done'
            ? html`<div class="step-done">
                ${ICON_CHECK}
                <span>Done</span>
                ${durationMs !== null
                  ? html`<span class="step-done-meta"
                      >· ${formatElapsed(durationMs)}</span
                    >`
                  : nothing}
              </div>`
            : nothing}
          ${stage.status === 'error' && stage.error
            ? html`<div class="step-error">
                ${ICON_ALERT}<span>${stage.error}</span>
              </div>`
            : nothing}
        </div>
      </div>
    `;
  }

  private _renderLiveEdge(): TemplateResult {
    return html`
      <div class="live-edge" aria-live="polite">
        <span
          class="live-edge-spinner"
          role="status"
          aria-label="Working"
        >${DOT_RING_ITEMS}</span>
        <span>Working…</span>
        <button class="cancel-link" @click=${this._cancel}>Cancel</button>
      </div>
    `;
  }

  private _renderSources(
    stageId: string,
    sources: ToolUseSource[]
  ): TemplateResult {
    const expanded = this.expandedSourceStageIds.has(stageId);
    return expanded
      ? this._renderSourcesExpanded(stageId, sources)
      : this._renderSourcesCollapsed(stageId, sources);
  }

  private _renderSourcesCollapsed(
    stageId: string,
    sources: ToolUseSource[]
  ): TemplateResult {
    const visible = sources.slice(0, 4);
    const rest = sources.length - visible.length;
    return html`
      <div class="step-sources">
        ${visible.map(
          (src, i) => html`
            <a
              class="source-chip"
              href=${src.url}
              target="_blank"
              rel="noopener noreferrer"
              @click=${stopPropagation}
              title=${this._chipTooltip(src, i + 1)}
            >
              <span class="source-chip-num" aria-hidden="true">${i + 1}</span>
              <span class="source-chip-label">${sourceChipLabel(src)}</span>
            </a>
          `
        )}
        ${rest > 0
          ? html`<button
              class="sources-toggle"
              data-stage-id=${stageId}
              @click=${this._onSourcesToggleClick}
              aria-expanded="false"
              aria-label="Show ${rest} more source${rest === 1 ? '' : 's'}"
            >
              +${rest} more
            </button>`
          : nothing}
      </div>
    `;
  }

  private _renderSourcesExpanded(
    stageId: string,
    sources: ToolUseSource[]
  ): TemplateResult {
    return html`
      <div class="step-sources-expanded">
        <ol class="step-sources-list">
          ${sources.map(
            (src, i) => html`
              <li class="source-line">
                <span class="source-line-num" aria-hidden="true">${i + 1}</span>
                <a
                  class="source-line-link"
                  href=${src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  @click=${stopPropagation}
                  title=${src.title || src.url}
                  >${shortSourceId(src)}</a
                >
                ${src.preprint
                  ? html`<span class="source-line-tag">preprint</span>`
                  : nothing}
              </li>
            `
          )}
        </ol>
        <button
          class="sources-less"
          data-stage-id=${stageId}
          @click=${this._onSourcesToggleClick}
          aria-expanded="true"
          aria-label="Show fewer sources"
        >
          Show less
        </button>
      </div>
    `;
  }

  private _chipTooltip(src: ToolUseSource, index: number): string {
    const parts: string[] = [`[${index}]`];
    if (src.title) parts.push(src.title);
    const domain = sourceDomain(src);
    if (domain) parts.push(`— ${domain}`);
    if (src.preprint) parts.push('(preprint)');
    return parts.join(' ');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-agent-stages': AgentStagesBlockComponent;
  }
}
